import { z } from 'zod';
import { targetStatusSchema } from './explore-board.js';

/**
 * Scheduled board delivery + threshold alerts.
 *
 * Firestore layout — three separate top-level collections, deliberately NOT
 * fields on the board doc:
 *
 *   exploreBoardSubscriptions/{subscriptionId}   ← config, one per board
 *   exploreBoardAlertState/{boardId}             ← last verdict per cell
 *   boardAlerts/health                           ← cron observability
 *
 * A subscription is its own doc so the cron can query "everything due on this
 * cadence" directly. Putting it on the board would force an unfiltered sweep of
 * every board on every run, and would mix operational state into a document the
 * autosave rewrites constantly.
 *
 * DELIVERY CHANNELS: the in-app bell plus the Slack DM mirror. There is no email
 * transport in this codebase, so "scheduled delivery" means Slack + bell — not
 * an emailed PDF. Adding email would be a separate integration.
 */

const isoDateString = z.string().datetime();

/** How often a digest goes out. Alerts are evaluated on their own cadence. */
export const boardDigestCadenceSchema = z.enum(['daily', 'weekly']);
export type BoardDigestCadence = z.infer<typeof boardDigestCadenceSchema>;

export const exploreBoardSubscriptionSchema = z.object({
  /** Doc id. One subscription per board, so this is the board id. */
  id: z.string().min(1).max(64),
  boardId: z.string().min(1).max(64),
  /** Owner of the board at subscribe time — the path segment the cron needs to
   *  read `users/{ownerEmail}/exploreBoards/{boardId}` without a lookup. */
  ownerEmail: z.string().email().max(120),
  /**
   * Who gets notified. The owner picks this explicitly rather than it defaulting
   * to the board's collaborators: whoever owns a KPI is not always whoever reads
   * the board. Cap mirrors the collaborator cap.
   */
  recipients: z.array(z.string().email().max(120)).max(20).default([]),
  /** Periodic digest of the board's indicator values. */
  includeDigest: z.boolean().default(false),
  cadence: boardDigestCadenceSchema.default('weekly'),
  /** Notify when a cell target crosses a threshold. Evaluated hourly. */
  includeAlerts: z.boolean().default(true),
  /** Master switch — keeps the config around while silencing it. */
  enabled: z.boolean().default(true),
  createdAt: isoDateString,
  updatedAt: isoDateString,
  /** Last digest send, for the cadence check. Null until the first one goes out. */
  lastDigestAt: isoDateString.nullable().default(null),
});
export type ExploreBoardSubscription = z.infer<typeof exploreBoardSubscriptionSchema>;

/** Body for `PUT /metric-hub/explore-boards/:id/subscription`. Server owns
 *  `id`/`boardId`/`ownerEmail`/timestamps, so the client cannot set them. */
export const exploreBoardSubscriptionUpdateSchema = z.object({
  recipients: z.array(z.string().email().max(120)).max(20),
  includeDigest: z.boolean(),
  cadence: boardDigestCadenceSchema,
  includeAlerts: z.boolean(),
  enabled: z.boolean(),
});
export type ExploreBoardSubscriptionUpdate = z.infer<typeof exploreBoardSubscriptionUpdateSchema>;

/**
 * One cell's last known verdict, so alerts fire on a TRANSITION rather than on
 * every hourly run.
 *
 * `resolvedFrom`/`resolvedTo` are the concrete dates the evaluation actually
 * queried. They are stored because a browser and the cron can legitimately
 * resolve the same relative preset on different UTC days — without the resolved
 * pair, "the cron said off-target but my board says on-target" is not
 * debuggable. See the time-zone note in `lib/explore-board-filters.ts`.
 */
export const exploreCellAlertStateSchema = z.object({
  cellId: z.string().min(1).max(64),
  status: targetStatusSchema,
  /** The measured value behind the verdict. */
  value: z.number().finite(),
  /** The target it was compared against, snapshotted. */
  target: z.number().finite(),
  resolvedFrom: z.string().max(10),
  resolvedTo: z.string().max(10),
  evaluatedAt: isoDateString,
});
export type ExploreCellAlertState = z.infer<typeof exploreCellAlertStateSchema>;

export const exploreBoardAlertStateSchema = z.object({
  boardId: z.string().min(1).max(64),
  /** Keyed by cell id. Capped alongside the 50-cell board cap. */
  cells: z.array(exploreCellAlertStateSchema).max(50).default([]),
  updatedAt: isoDateString,
});
export type ExploreBoardAlertState = z.infer<typeof exploreBoardAlertStateSchema>;

/**
 * Cron health snapshot at `boardAlerts/health`. Same shape as the other crons'
 * (`metricConsumerDigest/health`, `metricPulse/health`): counters plus a named
 * `skippedReason` so a no-op run is explainable rather than silent.
 */
export const boardAlertsHealthSchema = z.object({
  ranAt: isoDateString,
  ranBy: z.string().max(160).default('board-alerts-cron'),
  /** Subscriptions considered. */
  subscriptions: z.number().int().nonnegative().default(0),
  /** Boards actually read + parsed. */
  boards: z.number().int().nonnegative().default(0),
  /** Cells whose target was evaluated (a query was run). */
  evaluated: z.number().int().nonnegative().default(0),
  /** Cells skipped because the query returned no usable value. */
  noData: z.number().int().nonnegative().default(0),
  /** Cells skipped because the query failed. */
  failed: z.number().int().nonnegative().default(0),
  /** Status transitions that produced a notification. */
  transitions: z.number().int().nonnegative().default(0),
  /** Digests sent this run. */
  digests: z.number().int().nonnegative().default(0),
  /** Notification recipients across alerts + digests. */
  recipients: z.number().int().nonnegative().default(0),
  /** True when the run stopped early because the semantic layer was saturated. */
  stoppedOnSaturation: z.boolean().default(false),
  skippedReason: z.string().max(200).nullable().default(null),
});
export type BoardAlertsHealth = z.infer<typeof boardAlertsHealthSchema>;

export const boardAlertsHealthResponseSchema = z.object({
  health: boardAlertsHealthSchema.nullable(),
});
export type BoardAlertsHealthResponse = z.infer<typeof boardAlertsHealthResponseSchema>;
